import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  createResearchAlert,
  deleteResearchAlert,
  fetchResearchAlertHistory,
  fetchResearchAlerts,
  previewResearchAlert,
  updateResearchAlert,
  type EmailDeliveryState,
  type ResearchAlert,
  type ResearchAlertCadence,
  type ResearchAlertChannels,
  type ResearchAlertCreateRequest,
  type ResearchAlertPreviewResponse,
  type ResearchAlertRetrievalMode,
  type ResearchAlertRun,
  type ResearchAlertsResponse,
} from "../lib/researchAlerts";
import { useCan } from "../lib/session";
import "../styles/digests.css";

const ALERTS_QUERY_KEY = ["research-alerts"] as const;
const CADENCES: Array<{ value: ResearchAlertCadence; label: string }> = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];
const RETRIEVAL_MODES: Array<{
  value: ResearchAlertRetrievalMode;
  label: string;
  detail: string;
}> = [
  { value: "hybrid", label: "Hybrid", detail: "meaning and exact terms" },
  { value: "keyword", label: "Keyword", detail: "exact terms" },
  { value: "vector", label: "Semantic", detail: "meaning" },
];

type BadgeTone = "green" | "blue" | "amber" | "red" | "gray";
type AlertFilter = "active" | "paused";

function appPath(path: string): string {
  return path.startsWith("/app/") ? path.slice(4) : path;
}

function parseDate(value: string, dateOnly: boolean): Date | null {
  const parsed = new Date(dateOnly && /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function LocalizedTime({
  value,
  fallback = "Not yet",
  dateOnly = false,
}: {
  value: string | null;
  fallback?: string;
  dateOnly?: boolean;
}) {
  if (!value) return <span>{fallback}</span>;
  const date = parseDate(value, dateOnly);
  if (!date) return <span>{fallback}</span>;
  const formatted = dateOnly
    ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date)
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
  return (
    <time dateTime={value} title={date.toLocaleString()}>
      {formatted}
    </time>
  );
}

function Badge({ tone = "gray", children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`digests-badge digests-badge--${tone}`}>{children}</span>;
}

function StateMessage({
  title,
  detail,
  action,
  error = false,
  live = false,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
  error?: boolean;
  live?: boolean;
}) {
  return (
    <div
      className={`digests-state${error ? " digests-state--error" : ""}`}
      role={error ? "alert" : live ? "status" : undefined}
    >
      <div>
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      {action}
    </div>
  );
}

function runPresentation(run: ResearchAlertRun | null): {
  label: string;
  tone: BadgeTone;
  detail: string;
} {
  if (!run) {
    return {
      label: "Not checked yet",
      tone: "gray",
      detail: "This alert has no stored check outcome yet.",
    };
  }
  switch (run.status) {
    case "pending":
      return run.failure_code
        ? {
            label: "Retry scheduled",
            tone: "amber",
            detail: "The prior attempt did not complete; Athena will retry the same evidence window.",
          }
        : {
            label: "Check queued or running",
            tone: "blue",
            detail: "The check is queued or in progress. Delivery state is not final yet.",
          };
    case "notified":
      return {
        label: "Notification created",
        tone: "green",
        detail: run.new_result_count == null
          ? "New matching evidence was found; the stored document count is unavailable."
          : `${run.new_result_count} new ${run.new_result_count === 1 ? "document" : "documents"} matched.`,
      };
    case "skipped_empty":
      return {
        label: "No new evidence",
        tone: "gray",
        detail: "The check completed without a newly ingested matching document.",
      };
    case "failed":
      return {
        label: "Check failed",
        tone: "red",
        detail: failureDetail(run.failure_code),
      };
    case "unknown":
      return {
        label: "Status unavailable",
        tone: "gray",
        detail: "Athena retained this check, but its outcome is not recognized by this portal version.",
      };
  }
}

function failureDetail(code: string | null): string {
  switch (code) {
    case "invalid_configuration":
      return "The saved scope is invalid. Recreate the alert with a supported query, ticker, source, and document type before relying on it.";
    case "result_cap_reached":
      return "The check found too many matches to process safely. Its evidence window was not advanced and no notification was delivered; narrow the scope before retrying.";
    case "search_failed":
      return "The document search did not complete, so no notification outcome was created. Retry the preview or wait for the next eligible check.";
    case "delivery_failed":
      return "The check completed, but delivery did not complete. Review the stored channel state and retry later.";
    default:
      return "The check did not complete. Its stored scope and history remain available.";
  }
}

function emailStateLabel(
  state: EmailDeliveryState,
  run: ResearchAlertRun | null,
  channels: ResearchAlertChannels,
): { label: string; tone: BadgeTone } {
  if (state === "sent") return { label: "Email sent", tone: "green" };
  if (state === "sending") return { label: "Email sending", tone: "blue" };
  if (state === "pending") return { label: "Email queued", tone: "blue" };
  if (state === "failed") return { label: "Email failed", tone: "red" };
  if (state === "unknown") return { label: "Email status unavailable", tone: "gray" };
  if (state === "suppressed") {
    return { label: "Email suppressed", tone: "amber" };
  }
  if (!run) {
    return channels.email === "suppressed"
      ? { label: "Email suppressed", tone: "amber" }
      : { label: "Email not delivered yet", tone: "gray" };
  }
  if (run.status === "skipped_empty") return { label: "Email not needed", tone: "gray" };
  if (run.status === "pending") return { label: "Email awaiting outcome", tone: "gray" };
  if (run.status === "failed") return { label: "Email not sent", tone: "gray" };
  return { label: "Email status unavailable", tone: "gray" };
}

function inboxStateLabel(run: ResearchAlertRun | null): { label: string; tone: BadgeTone } {
  if (run?.delivery.inbox === "created") return { label: "Inbox created", tone: "green" };
  if (!run) return { label: "Inbox not created yet", tone: "gray" };
  if (run.status === "skipped_empty") return { label: "Inbox not needed", tone: "gray" };
  if (run.status === "pending") return { label: "Inbox awaiting outcome", tone: "gray" };
  if (run.status === "failed") return { label: "Inbox not created", tone: "gray" };
  return { label: "Inbox status unavailable", tone: "gray" };
}

function isOverdue(alert: ResearchAlert): boolean {
  if (!alert.digest_enabled || !alert.next_eligible_at || alert.latest_run?.status === "pending") {
    return false;
  }
  const due = Date.parse(alert.next_eligible_at);
  return Number.isFinite(due) && due < Date.now();
}

function cadenceLabel(cadence: ResearchAlertCadence | null): string {
  return CADENCES.find((option) => option.value === cadence)?.label ?? "Cadence unavailable";
}

function retrievalLabel(mode: ResearchAlertRetrievalMode): string {
  return RETRIEVAL_MODES.find((option) => option.value === mode)?.label ?? mode;
}

function trapDialogTab(event: KeyboardEvent<HTMLElement>, container: HTMLElement | null) {
  if (event.key !== "Tab" || !container) return;
  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(
      "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])",
    ),
  ).filter((element) => !element.hasAttribute("hidden"));
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function updateAlertCache(
  queryClient: ReturnType<typeof useQueryClient>,
  alert: ResearchAlert,
  runtimeAvailable: boolean,
) {
  queryClient.setQueryData<ResearchAlertsResponse>(ALERTS_QUERY_KEY, (current) => {
    if (!current) return current;
    const exists = current.saved_searches.some(
      (item) => item.saved_search_id === alert.saved_search_id,
    );
    const savedSearches = exists
      ? current.saved_searches.map((item) =>
          item.saved_search_id === alert.saved_search_id ? alert : item,
        )
      : [alert, ...current.saved_searches];
    return {
      ...current,
      saved_searches: savedSearches,
      count: savedSearches.length,
      runtime_available: runtimeAvailable,
    };
  });
}

function NewAlertForm({
  onCreated,
  onCancel,
  runtimeAvailable,
}: {
  onCreated: (alert: ResearchAlert, runtimeAvailable: boolean) => void;
  onCancel: () => void;
  runtimeAvailable: boolean;
}) {
  const queryClient = useQueryClient();
  const nameInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [ticker, setTicker] = useState("");
  const [source, setSource] = useState("");
  const [docType, setDocType] = useState("");
  const [retrievalMode, setRetrievalMode] = useState<ResearchAlertRetrievalMode>("hybrid");
  const [cadence, setCadence] = useState<ResearchAlertCadence>("daily");

  useEffect(() => {
    nameInput.current?.focus();
  }, []);

  const create = useMutation({
    mutationFn: createResearchAlert,
    onSuccess: (response) => {
      updateAlertCache(queryClient, response.saved_search, response.runtime_available);
      onCreated(response.saved_search, response.runtime_available);
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const request: ResearchAlertCreateRequest = {
      name: name.trim(),
      query: query.trim(),
      ticker: ticker.trim() ? ticker.trim().toUpperCase() : null,
      source: source.trim() || null,
      doc_type: docType.trim() || null,
      filters: { retrieval_mode: retrievalMode },
      digest_enabled: true,
      digest_cadence: cadence,
    };
    if (!request.name || !request.query) return;
    create.mutate(request);
  };

  return (
    <section className="digests-create" aria-labelledby="digests-create-title">
      <header>
        <div>
          <span className="digests-kicker">New standing question</span>
          <h2 id="digests-create-title">Create a research alert</h2>
          <p>
            Define the exact document scope Athena should check for newly ingested evidence.
            {!runtimeAvailable ? " The configuration will be ready when scheduled checking returns." : ""}
          </p>
        </div>
      </header>
      <form onSubmit={submit}>
        <div className="digests-form-grid">
          <label>
            <span>Alert name <b aria-hidden="true">*</b></span>
            <input
              ref={nameInput}
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="TTD retail-media pressure"
              autoComplete="off"
            />
          </label>
          <label>
            <span>Ticker</span>
            <input
              value={ticker}
              onChange={(event) => setTicker(event.target.value)}
              placeholder="TTD"
              autoCapitalize="characters"
              autoComplete="off"
            />
            <small>Blank checks all tickers.</small>
          </label>
          <label className="digests-form-wide">
            <span>Evidence question or search terms <b aria-hidden="true">*</b></span>
            <textarea
              required
              rows={3}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Amazon DSP pricing pressure and agency budget shifts"
            />
          </label>
          <label>
            <span>Source ID</span>
            <input
              value={source}
              onChange={(event) => setSource(event.target.value)}
              placeholder="sec-edgar"
              autoComplete="off"
            />
            <small>Exact source ID; blank means every source.</small>
          </label>
          <label>
            <span>Document type</span>
            <input
              value={docType}
              onChange={(event) => setDocType(event.target.value)}
              placeholder="10-k"
              autoComplete="off"
            />
            <small>Exact document type; blank means every type.</small>
          </label>
          <label>
            <span>Match method</span>
            <select
              value={retrievalMode}
              onChange={(event) =>
                setRetrievalMode(event.target.value as ResearchAlertRetrievalMode)
              }
            >
              {RETRIEVAL_MODES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label} — {option.detail}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Check cadence</span>
            <select
              value={cadence}
              onChange={(event) => setCadence(event.target.value as ResearchAlertCadence)}
            >
              {CADENCES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        {create.isError ? (
          <div className="digests-inline-warning" role="alert">
            Athena could not confirm that the alert was created. The alert list is refreshing before you retry.
          </div>
        ) : null}
        <div className="digests-form-actions">
          <button className="btn" type="button" disabled={create.isPending} onClick={onCancel}>
            Cancel
          </button>
          <button className="btn primary" type="submit" disabled={create.isPending}>
            {create.isPending
              ? "Creating…"
              : runtimeAvailable
                ? "Create active alert"
                : "Save configured alert"}
          </button>
        </div>
      </form>
    </section>
  );
}

function ChannelState({ run, channels }: { run: ResearchAlertRun | null; channels: ResearchAlertChannels }) {
  const inbox = inboxStateLabel(run);
  const email = emailStateLabel(run?.delivery.email ?? null, run, channels);
  return (
    <div className="digests-channels" aria-label="Latest channel state">
      <Badge tone={inbox.tone}>{inbox.label}</Badge>
      <Badge tone={email.tone}>{email.label}</Badge>
    </div>
  );
}

function RunItem({ run, channels }: { run: ResearchAlertRun; channels: ResearchAlertChannels }) {
  const outcome = runPresentation(run);
  return (
    <li>
      <article className="digests-run">
        <div className="digests-run-outcome">
          <Badge tone={outcome.tone}>{outcome.label}</Badge>
          <strong>
            <LocalizedTime value={run.executed_at ?? run.created_at} fallback="Time unavailable" />
          </strong>
        </div>
        <p>{outcome.detail}</p>
        <ChannelState run={run} channels={channels} />
        {run.window_from || run.window_to ? (
          <div className="digests-run-window">
            Evidence window: <LocalizedTime value={run.window_from} fallback="Start unavailable" />
            {" → "}
            <LocalizedTime value={run.window_to} fallback="End unavailable" />
          </div>
        ) : null}
      </article>
    </li>
  );
}

function AlertHistory({
  alert,
  channels,
}: {
  alert: ResearchAlert;
  channels: ResearchAlertChannels;
}) {
  const history = useInfiniteQuery({
    queryKey: ["research-alert-history", alert.saved_search_id],
    queryFn: ({ pageParam }) => fetchResearchAlertHistory(alert.saved_search_id, pageParam, 8),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    retry: false,
  });

  if (history.isPending) {
    return (
      <StateMessage
        title="Loading check history"
        detail="Retrieving earlier outcomes for this alert…"
        live
      />
    );
  }
  if (history.isError && !history.data) {
    return (
      <StateMessage
        title="Check history could not be loaded"
        detail="The alert remains available. Retry this history request."
        error
        action={
          <button className="btn" type="button" onClick={() => void history.refetch()}>
            Retry history
          </button>
        }
      />
    );
  }

  const seen = new Set<string>();
  const runs = (history.data?.pages ?? [])
    .flatMap((page) => page.runs)
    .filter((run) => {
      if (seen.has(run.digest_run_id)) return false;
      seen.add(run.digest_run_id);
      return true;
    });
  const total = history.data?.pages[0]?.total_count ?? runs.length;

  return (
    <div className="digests-history" aria-busy={history.isFetching}>
      <div className="digests-history-summary">
        Showing {runs.length} of {total} stored {total === 1 ? "check" : "checks"}
        {history.isFetching && !history.isFetchingNextPage ? " · Refreshing…" : ""}
      </div>
      {history.isError ? (
        <div className="digests-inline-warning" role="alert">
          Newer history could not be retrieved. Showing the checks already loaded.
          <button className="btn" type="button" onClick={() => void history.refetch()}>
            Retry
          </button>
        </div>
      ) : null}
      {runs.length ? (
        <ol className="digests-run-list" aria-label={`Check history for ${alert.name}`}>
          {runs.map((run) => (
            <RunItem key={run.digest_run_id} run={run} channels={channels} />
          ))}
        </ol>
      ) : (
        <StateMessage
          title="No checks yet"
          detail="This alert is untested until its first scheduled check completes."
        />
      )}
      {history.hasNextPage ? (
        <button
          className="btn digests-load-more"
          type="button"
          disabled={history.isFetchingNextPage}
          onClick={() => void history.fetchNextPage()}
        >
          {history.isFetchingNextPage ? "Loading…" : "Load earlier checks"}
        </button>
      ) : null}
      {history.isFetchNextPageError ? (
        <div className="digests-inline-warning" role="alert">
          Earlier checks could not be loaded.
          <button className="btn" type="button" onClick={() => void history.fetchNextPage()}>
            Retry earlier checks
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PreviewPanel({
  preview,
  panelRef,
  onClose,
}: {
  preview: ResearchAlertPreviewResponse;
  panelRef: React.RefObject<HTMLElement>;
  onClose: () => void;
}) {
  return (
    <section
      ref={panelRef}
      className="digests-preview"
      aria-label={`Preview for ${preview.name}`}
      tabIndex={-1}
    >
      <header>
        <div>
          <span className="digests-kicker">Unsent preview</span>
          <h4>{preview.result_count} newly ingested {preview.result_count === 1 ? "document" : "documents"}</h4>
        </div>
        <button className="btn" type="button" onClick={onClose}>
          Close preview
        </button>
      </header>
      <p className="digests-preview-window">
        Checked from <LocalizedTime value={preview.window.from} fallback="Start unavailable" /> to{" "}
        <LocalizedTime value={preview.window.to} fallback="End unavailable" />.
        {!preview.cursor_active
          ? " This is a bounded first-check window because the alert has no active cursor yet."
          : ""}
      </p>
      {preview.results.length === 0 ? (
        <StateMessage
          title="No notification would be created"
          detail="No newly ingested document matches this alert in the preview window."
        />
      ) : (
        <ul className="digests-preview-list">
          {preview.results.map((item) => (
            <li key={item.document_id}>
              <Link to={appPath(item.deep_link)}>{item.title}</Link>
              <div>
                <span>{item.source_name ?? "Source unavailable"}</span>
                {item.ticker ? <span>{item.ticker}</span> : null}
                {item.page != null ? <span>Page {item.page}</span> : null}
                <span>
                  Document date <LocalizedTime value={item.document_date} dateOnly fallback="unavailable" />
                </span>
                <span>
                  Ingested <LocalizedTime value={item.ingested_at} fallback="time unavailable" />
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
      {preview.truncated ? (
        <div className="digests-inline-warning" role="alert">
          This scope reached the preview safety bound, so Athena would not create a notification. Narrow the ticker, source, document type, or query before relying on it.
        </div>
      ) : preview.results.length > 0 && !preview.would_send ? (
        <div className="digests-inline-warning" role="status">
          Matching documents are shown, but the server reports that no notification would be created.
        </div>
      ) : null}
    </section>
  );
}

function AlertCard({
  alert,
  channels,
  writable,
  runtimeAvailable,
  onDeleted,
  onChanged,
}: {
  alert: ResearchAlert;
  channels: ResearchAlertChannels;
  writable: boolean;
  runtimeAvailable: boolean;
  onDeleted: (name: string) => void;
  onChanged: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [preview, setPreview] = useState<ResearchAlertPreviewResponse | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const previewPanel = useRef<HTMLElement>(null);
  const previewButton = useRef<HTMLButtonElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const cancelDeleteButton = useRef<HTMLButtonElement>(null);
  const confirmDialog = useRef<HTMLElement>(null);
  const dialogTitleId = useId();
  const dialogDetailId = useId();
  const outcome = runtimeAvailable
    ? runPresentation(alert.latest_run)
    : {
        label: "Checks unavailable",
        tone: "gray" as const,
        detail: "Scheduled checking is temporarily unavailable. The stored alert scope is unchanged.",
      };
  const overdue = runtimeAvailable && isOverdue(alert);

  const update = useMutation({
    mutationFn: (request: Parameters<typeof updateResearchAlert>[1]) =>
      updateResearchAlert(alert.saved_search_id, request),
    onSuccess: (response, request) => {
      updateAlertCache(queryClient, response.saved_search, response.runtime_available);
      const changedState = !response.runtime_available
        ? `${alert.name} change saved; checks are temporarily unavailable.`
        : request.digest_enabled === false
          ? `${alert.name} paused.`
          : request.digest_enabled === true
            ? `${alert.name} resumed.`
            : `${alert.name} cadence updated.`;
      onChanged(changedState);
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteResearchAlert(alert.saved_search_id),
    onSuccess: () => {
      queryClient.setQueryData<ResearchAlertsResponse>(ALERTS_QUERY_KEY, (current) => {
        if (!current) return current;
        const savedSearches = current.saved_searches.filter(
          (item) => item.saved_search_id !== alert.saved_search_id,
        );
        return { ...current, saved_searches: savedSearches, count: savedSearches.length };
      });
      setConfirmingDelete(false);
      onDeleted(alert.name);
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => previewResearchAlert(alert.saved_search_id),
    onSuccess: (response) => {
      setPreview(response);
      window.setTimeout(() => previewPanel.current?.focus());
    },
  });

  const closePreview = useCallback(() => {
    setPreview(null);
    window.setTimeout(() => previewButton.current?.focus());
  }, []);

  const restoreDeleteFocus = useCallback(() => {
    window.setTimeout(() => deleteButton.current?.focus());
  }, []);

  useEffect(() => {
    if (!confirmingDelete) return;
    cancelDeleteButton.current?.focus();
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || remove.isPending) return;
      event.preventDefault();
      setConfirmingDelete(false);
      restoreDeleteFocus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirmingDelete, remove.isPending, restoreDeleteFocus]);

  const scopeItems: Array<{ label: string; value: ReactNode }> = [
    { label: "Query", value: alert.query },
    { label: "Ticker", value: alert.ticker ?? "All tickers" },
    { label: "Source", value: alert.source ?? "All sources" },
    { label: "Document type", value: alert.doc_type ?? "All document types" },
    ...(alert.date_from
      ? [{
          label: "Document date from",
          value: <LocalizedTime value={alert.date_from} dateOnly fallback="Unavailable" />,
        }]
      : []),
    ...(alert.date_to
      ? [{
          label: "Document date through",
          value: <LocalizedTime value={alert.date_to} dateOnly fallback="Unavailable" />,
        }]
      : []),
    { label: "Match", value: retrievalLabel(alert.retrieval_mode) },
  ];

  return (
    <li>
      <article className={`digests-alert${overdue ? " digests-alert--overdue" : ""}`}>
        <header className="digests-alert-header">
          <div>
            <div className="digests-alert-titleline">
              <h3>{alert.name}</h3>
              <Badge tone={alert.digest_enabled && runtimeAvailable ? "green" : "gray"}>
                {alert.digest_enabled
                  ? runtimeAvailable
                    ? "Active"
                    : "Configured"
                  : "Paused"}
              </Badge>
              {overdue ? <Badge tone="amber">Check overdue</Badge> : null}
            </div>
            <p>
              {alert.digest_enabled
                ? runtimeAvailable
                  ? `${cadenceLabel(alert.digest_cadence)} check for newly ingested evidence.`
                  : `Configured for ${cadenceLabel(alert.digest_cadence).toLowerCase()} checks when scheduled checking returns.`
                : "Future checks are paused; stored scope and history remain available."}
            </p>
          </div>
          {writable ? (
            <div className="digests-alert-actions">
              <button
                className="btn"
                type="button"
                disabled={!runtimeAvailable || update.isPending || remove.isPending}
                onClick={() =>
                  update.mutate({
                    digest_enabled: !alert.digest_enabled,
                    digest_cadence: alert.digest_cadence ?? "daily",
                  })
                }
              >
                {!runtimeAvailable
                  ? "Scheduling unavailable"
                  : update.isPending
                  ? "Saving…"
                  : alert.digest_enabled
                    ? "Pause alert"
                    : "Resume alert"}
              </button>
              <button
                ref={deleteButton}
                className="btn digests-danger-action"
                type="button"
                disabled={remove.isPending}
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </button>
            </div>
          ) : null}
        </header>

        <dl
          className={`digests-scope digests-scope--${scopeItems.length}`}
          aria-label={`Supported scope for ${alert.name}`}
        >
          {scopeItems.map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>

        <section className="digests-latest" aria-label={`Latest outcome for ${alert.name}`}>
          <div className="digests-latest-outcome">
            <span>Latest outcome</span>
            <div>
              <Badge tone={outcome.tone}>{outcome.label}</Badge>
              <span>{outcome.detail}</span>
            </div>
          </div>
          {runtimeAvailable ? (
            <ChannelState run={alert.latest_run} channels={channels} />
          ) : (
            <div className="digests-channels" aria-label="Latest channel state">
              <Badge>Inbox outcome unavailable</Badge>
              <Badge>Email outcome unavailable</Badge>
            </div>
          )}
          <dl className="digests-timing">
            <div>
              <dt>Last checked</dt>
              <dd>
                {runtimeAvailable ? (
                  <LocalizedTime value={alert.digest_last_checked_at} />
                ) : (
                  "Unavailable while checks are offline"
                )}
              </dd>
            </div>
            <div>
              <dt>{alert.digest_enabled ? "Next eligible" : "Paused"}</dt>
              <dd>
                {!runtimeAvailable ? (
                  "Unavailable while checks are offline"
                ) : alert.digest_enabled ? (
                  <LocalizedTime value={alert.next_eligible_at} fallback="Not scheduled yet" />
                ) : (
                  "No future check scheduled"
                )}
              </dd>
            </div>
          </dl>
        </section>

        {writable ? (
          <div className="digests-management">
            <label>
              <span>Cadence</span>
              <select
                value={alert.digest_cadence ?? "daily"}
                disabled={!runtimeAvailable || update.isPending}
                aria-label={`Cadence for ${alert.name}`}
                onChange={(event) =>
                  update.mutate({ digest_cadence: event.target.value as ResearchAlertCadence })
                }
              >
                {CADENCES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              ref={previewButton}
              className="btn"
              type="button"
              disabled={!runtimeAvailable || previewMutation.isPending}
              onClick={() => previewMutation.mutate()}
            >
              {!runtimeAvailable
                ? "Preview unavailable"
                : previewMutation.isPending
                  ? "Previewing…"
                  : "Preview new evidence"}
            </button>
          </div>
        ) : null}

        {update.isError ? (
          <div className="digests-inline-warning" role="alert">
            Athena could not confirm the change. The alert list is refreshing before you retry.
          </div>
        ) : null}
        {previewMutation.isError ? (
          <div className="digests-inline-warning" role="alert">
            Preview could not be loaded. The alert was not changed.
            <button className="btn" type="button" onClick={() => previewMutation.mutate()}>
              Retry preview
            </button>
          </div>
        ) : null}
        {preview ? (
          <PreviewPanel preview={preview} panelRef={previewPanel} onClose={closePreview} />
        ) : null}

        {runtimeAvailable ? (
          <>
            <div className="digests-history-toggle">
              <button
                className="btn"
                type="button"
                aria-expanded={historyOpen}
                onClick={() => setHistoryOpen((open) => !open)}
              >
                {historyOpen ? "Hide check history" : "Show check history"}
              </button>
            </div>
            {historyOpen ? <AlertHistory alert={alert} channels={channels} /> : null}
          </>
        ) : (
          <div className="digests-history-unavailable">Check history is temporarily unavailable.</div>
        )}
      </article>

      {confirmingDelete ? (
        <div
          className="digests-dialog-backdrop"
          onMouseDown={() => {
            if (remove.isPending) return;
            setConfirmingDelete(false);
            restoreDeleteFocus();
          }}
        >
          <section
            ref={confirmDialog}
            className="digests-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
            aria-describedby={dialogDetailId}
            aria-busy={remove.isPending}
            onMouseDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => trapDialogTab(event, confirmDialog.current)}
          >
            <h2 id={dialogTitleId}>Delete this research alert?</h2>
            <p id={dialogDetailId}>
              Athena will permanently delete “{alert.name}”, its saved scope, and its check history. Stored notification records may remain in operational audit history, but this alert cannot be resumed.
            </p>
            {remove.isError ? (
              <div className="digests-inline-warning" role="alert">
                Athena could not confirm deletion. The alert list is refreshing before you retry.
              </div>
            ) : null}
            <div className="digests-confirm-actions">
              <button
                ref={cancelDeleteButton}
                className="btn"
                type="button"
                disabled={remove.isPending}
                onClick={() => {
                  setConfirmingDelete(false);
                  restoreDeleteFocus();
                }}
              >
                Cancel
              </button>
              <button
                className="btn digests-danger-button"
                type="button"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {remove.isPending ? "Deleting…" : "Delete alert"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </li>
  );
}

export default function Digests() {
  const writable = useCan("research.write");
  const [filter, setFilter] = useState<AlertFilter>("active");
  const [creating, setCreating] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const newAlertButton = useRef<HTMLButtonElement>(null);
  const listHeading = useRef<HTMLHeadingElement>(null);
  const alerts = useQuery({
    queryKey: ALERTS_QUERY_KEY,
    queryFn: fetchResearchAlerts,
    retry: false,
    staleTime: 30_000,
  });

  const closeCreate = () => {
    setCreating(false);
    window.setTimeout(() => newAlertButton.current?.focus());
  };

  if (alerts.isPending) {
    return (
      <div className="digests-page" aria-busy="true">
        <header className="digests-pagehead">
          <div>
            <span className="digests-eyebrow">Evidence operations</span>
            <h1>Research alerts</h1>
            <p>Standing document questions with truthful check and delivery outcomes.</p>
          </div>
        </header>
        <StateMessage
          title="Loading research alerts"
          detail="Retrieving saved scope, latest outcomes, and channel state…"
          live
        />
      </div>
    );
  }

  if (alerts.isError && !alerts.data) {
    return (
      <div className="digests-page">
        <header className="digests-pagehead">
          <div>
            <span className="digests-eyebrow">Evidence operations</span>
            <h1>Research alerts</h1>
            <p>Standing document questions with truthful check and delivery outcomes.</p>
          </div>
        </header>
        <StateMessage
          title="Research alerts could not be loaded"
          detail="No alert state is shown because Athena could not retrieve it. Retry the request."
          error
          action={
            <button className="btn" type="button" onClick={() => void alerts.refetch()}>
              Retry alerts
            </button>
          }
        />
      </div>
    );
  }

  const data = alerts.data;
  if (!data) return null;
  const active = data.saved_searches.filter((alert) => alert.digest_enabled);
  const paused = data.saved_searches.filter((alert) => !alert.digest_enabled);
  const visible = filter === "active" ? active : paused;

  const handleDeleted = (name: string) => {
    setAnnouncement(`${name} deleted.`);
    window.setTimeout(() => listHeading.current?.focus());
  };

  return (
    <div className="digests-page" aria-busy={alerts.isFetching}>
      <div className="digests-live" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>
      <header className="digests-pagehead">
        <div>
          <span className="digests-eyebrow">Evidence operations</span>
          <h1>Research alerts</h1>
          <p>
            Ask a standing evidence question. Athena checks newly ingested matching documents, creates an inbox notification when warranted, and reports email separately.
          </p>
        </div>
        <div className="digests-page-actions">
          <span className="digests-refresh-state" role="status" aria-live="polite">
            {alerts.isFetching ? "Refreshing…" : "Alert state current"}
          </span>
          <button
            className="btn"
            type="button"
            disabled={alerts.isFetching}
            onClick={() => void alerts.refetch()}
          >
            Refresh
          </button>
          {writable ? (
            <button
              ref={newAlertButton}
              className="btn primary"
              type="button"
              aria-expanded={creating}
              onClick={() => setCreating((open) => !open)}
            >
              {creating ? "Close new alert" : "New alert"}
            </button>
          ) : null}
        </div>
      </header>

      <section className="digests-channel-banner" aria-label="Available notification channels">
        <div>
          <strong>Delivery channels</strong>
          <span>Inbox is available for matching evidence.</span>
        </div>
        <div>
          <Badge tone="green">Inbox available</Badge>
          <Badge tone={data.channels.email === "queued" ? "blue" : "amber"}>
            {data.channels.email === "queued" ? "Email queue available" : "Email suppressed"}
          </Badge>
        </div>
      </section>

      {!data.runtime_available ? (
        <div className="digests-runtime-note" role="note">
          <Badge>Checks temporarily unavailable</Badge>
          <span>
            Saved alert configuration is available, but scheduling, previews, current outcomes, and check history will return after the digest runtime is restored.
          </span>
        </div>
      ) : null}

      {!writable ? (
        <div className="digests-readonly" role="note">
          <Badge>Read only</Badge>
          <span>You can inspect scope, stored check history, and delivery state. Research write access is required to create or change alerts.</span>
        </div>
      ) : null}

      {creating && writable ? (
        <NewAlertForm
          runtimeAvailable={data.runtime_available}
          onCancel={closeCreate}
          onCreated={(alert, runtimeAvailable) => {
            setCreating(false);
            setFilter("active");
            setAnnouncement(
              runtimeAvailable
                ? `${alert.name} created and active.`
                : `${alert.name} configuration saved; checks are temporarily unavailable.`,
            );
            window.setTimeout(() => listHeading.current?.focus());
          }}
        />
      ) : null}

      {alerts.isError ? (
        <div className="digests-inline-warning" role="alert">
          Athena could not refresh the alert list. Showing the last successfully loaded state.
          <button className="btn" type="button" onClick={() => void alerts.refetch()}>
            Retry refresh
          </button>
        </div>
      ) : null}

      <section className="digests-workspace" aria-labelledby="digests-list-title">
        <header className="digests-workspace-header">
          <div>
            <h2 ref={listHeading} id="digests-list-title" tabIndex={-1}>
              Alert book
            </h2>
            <p>One row per standing question; expand history only when you need the audit trail.</p>
          </div>
          <div className="digests-filter" role="group" aria-label="Filter alerts by state">
            <button
              type="button"
              aria-pressed={filter === "active"}
              onClick={() => setFilter("active")}
            >
              {data.runtime_available ? "Active" : "Configured"} <span>{active.length}</span>
            </button>
            <button
              type="button"
              aria-pressed={filter === "paused"}
              onClick={() => setFilter("paused")}
            >
              Paused <span>{paused.length}</span>
            </button>
          </div>
        </header>

        {visible.length ? (
          <ul
            className="digests-alert-list"
            aria-label={`${filter === "active" && !data.runtime_available ? "configured" : filter} research alerts`}
          >
            {visible.map((alert) => (
              <AlertCard
                key={alert.saved_search_id}
                alert={alert}
                channels={data.channels}
                writable={writable}
                runtimeAvailable={data.runtime_available}
                onDeleted={handleDeleted}
                onChanged={setAnnouncement}
              />
            ))}
          </ul>
        ) : (
          <StateMessage
            title={
              filter === "active"
                ? data.runtime_available
                  ? "No active alerts"
                  : "No configured alerts"
                : "No paused alerts"
            }
            detail={
              filter === "active"
                ? writable
                  ? data.runtime_available
                    ? "Create an alert to start checking a standing evidence question."
                    : "Save an alert configuration now so it is ready when scheduled checking returns."
                  : "No active research alerts are available to this session."
                : "Paused alerts retain their scope and history and can be resumed by a research writer."
            }
            action={
              filter === "active" && writable && !creating ? (
                <button className="btn primary" type="button" onClick={() => setCreating(true)}>
                  Create first alert
                </button>
              ) : undefined
            }
          />
        )}
      </section>
    </div>
  );
}
